'use client';

import {registerCustomer} from '@/app/(auth)/join/actions';
import CreatePasswordRequirements from '@/components/createPasswordRequirements/CreatePasswordRequirements';
import ErrorMessage from '@/components/errorMessage/ErrorMessage';
import LoadingButton from '@/components/loadingButton/LoadingButton';
import PasswordInput from '@/components/passwordInput/PasswordInput';
import {Checkbox} from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useInvite from '@/hooks/use-invite';
import {cn} from '@/lib/utils';
import {
  isPhoneValidForSmsOptIn,
  RegistrationSchema,
  SMS_PHONE_REQUIRED_MESSAGE,
  type Registration,
} from '@/types/registration';
import {zodResolver} from '@hookform/resolvers/zod';
import {InputMask} from '@react-input/mask';
import Link from 'next/link';
import {useSearchParams} from 'next/navigation';
import {useState, useTransition} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'sonner';

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = CURRENT_YEAR - 16;
const MAX_YEAR = CURRENT_YEAR - 100;
const YEARS = Array.from({length: MIN_YEAR - MAX_YEAR + 1}, (_, i) => MIN_YEAR - i);

export default function JoinForm() {
  const [loading, startTransition] = useTransition();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const {inviteeEmail: email, code: inviteCode} = useInvite() ?? {};
  const form = useForm<Registration>({
    mode: 'onBlur',
    resolver: zodResolver(RegistrationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: email ?? '',
      phoneNumber: '',
      birthMonth: '',
      birthDay: '',
      birthYear: '',
      password: '',
      emailTransactional: true,
      emailPromotional: true,
      smsTransactional: false,
      smsPromotional: false,
      inviteCode,
    },
  });
  const emailIsReadonly = Boolean(email);
  const isPasswordDirty = form.getFieldState('password').isDirty;
  const password = form.watch('password');
  const birthdayLabelClassNames = cn({
    'text-destructive': form.formState.errors.birthYear?.message,
  });

  function handleOnSubmit(values: Registration) {
    setErrorMessage(null);

    startTransition(async () => {
      // Get all current search params
      const searchParamsObj = Object.fromEntries(searchParams.entries());

      // Determine the redirect path based on household invite status
      let redirectTo = '/otp';

      // Prepare registration data
      let registrationData = {...values};

      // Enrollment rules: always opt in to both marketing and transactional emails
      registrationData = {
        ...registrationData,
        emailTransactional: true,
        emailPromotional: true,
      };

      // For HOUSEHOLD_FULL invites, remove inviteCode and don't include search params in redirect
      if (searchParamsObj.s === 'HOUSEHOLD_FULL') {
        registrationData = {...registrationData, inviteCode: undefined};
      } else if (searchParams.toString()) {
        // For other cases, include search params in redirect
        redirectTo += `?${searchParams.toString()}`;
      }

      const {error, type} = await registerCustomer({
        ...registrationData,
        redirectTo,
      });

      if (error) {
        setErrorMessage(error);
      }

      if (type === 'existingAccount') {
        form.setError('email', {
          type: 'manual',
          message: 'An account has already been created for this email.',
        });
        form.setFocus('email');
      }

      if (type === 'existingPhoneNumber') {
        form.setError('phoneNumber', {
          type: 'manual',
          message: 'This phone number is already in use. Click contact us below for help.',
        });
        form.setFocus('phoneNumber');
      }
    });
  }

  function handleOnError() {
    toast.error('Please correct the errors in the form');
  }

  function validateSmsPhoneRequirement() {
    if (isPhoneValidForSmsOptIn(form.getValues())) {
      const phoneError = form.formState.errors.phoneNumber;
      if (phoneError?.message === SMS_PHONE_REQUIRED_MESSAGE) {
        form.clearErrors('phoneNumber');
      }
      return;
    }

    form.setError('phoneNumber', {
      type: 'manual',
      message: SMS_PHONE_REQUIRED_MESSAGE,
    });
  }

  return (
    <Form {...form}>
      <form
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(handleOnSubmit, handleOnError)}
        className="flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="firstName"
          render={({field}) => (
            <FormItem>
              <FormLabel>First name</FormLabel>
              <FormControl>
                <Input autoComplete="given-name" {...field} />
              </FormControl>
              <FormDescription className="sr-only">Please enter your first name.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({field}) => (
            <FormItem>
              <FormLabel>Last name</FormLabel>
              <FormControl>
                <Input autoComplete="family-name" {...field} aria-describedby="" />
              </FormControl>
              <FormDescription className="sr-only">Please enter your last name.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({field}) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input autoComplete="email" type="email" readOnly={emailIsReadonly} {...field} />
              </FormControl>
              <FormDescription>
                Email address must be the same as used for #1 Cochran services.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({field}) => (
            <FormItem>
              <FormLabel>Mobile number</FormLabel>
              <FormControl>
                <InputMask
                  component={Input}
                  mask="(###) ###-####"
                  replacement={{'#': /[0-9]/}}
                  autoComplete="tel"
                  type="tel"
                  value={field.value}
                  onChange={(event) => {
                    field.onChange(event);
                    validateSmsPhoneRequirement();
                  }}
                  onBlur={() => {
                    field.onBlur();
                    validateSmsPhoneRequirement();
                  }}
                />
              </FormControl>
              <FormDescription className="sr-only">Please enter your phone number.</FormDescription>
              <FormDescription>
                Mobile number is not required and will only be used to look up your account unless
                text communications are authorized below.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col gap-2">
          <Label className={birthdayLabelClassNames}>
            Birthday{' '}
            <span className="mb-1 mt-2 block text-xs text-gray-600">
              Must be 16 years or older to sign up for #1 Cochran ClubOne Rewards.
            </span>
          </Label>
          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="birthMonth"
              render={({field}) => (
                <FormItem className="flex-1">
                  <FormLabel className="sr-only">Month</FormLabel>
                  <Select
                    autoComplete="bday-month"
                    defaultValue={field.value}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Month" />
                      </SelectTrigger>
                    </FormControl>
                    <FormDescription className="sr-only">
                      Please select your birth month.
                    </FormDescription>
                    <SelectContent>
                      <SelectItem value="01">January</SelectItem>
                      <SelectItem value="02">February</SelectItem>
                      <SelectItem value="03">March</SelectItem>
                      <SelectItem value="04">April</SelectItem>
                      <SelectItem value="05">May</SelectItem>
                      <SelectItem value="06">June</SelectItem>
                      <SelectItem value="07">July</SelectItem>
                      <SelectItem value="08">August</SelectItem>
                      <SelectItem value="09">September</SelectItem>
                      <SelectItem value="10">October</SelectItem>
                      <SelectItem value="11">November</SelectItem>
                      <SelectItem value="12">December</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="birthDay"
              render={({field}) => (
                <FormItem className="flex-1">
                  <FormLabel className="sr-only">Day</FormLabel>
                  <Select
                    autoComplete="bday-day"
                    defaultValue={field.value}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Day" />
                      </SelectTrigger>
                    </FormControl>
                    <FormDescription className="sr-only">
                      Please select your birth day.
                    </FormDescription>
                    <SelectContent>
                      {Array.from({length: 31}, (_, i) => i + 1).map((day) => (
                        <SelectItem key={day} value={String(day)}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="birthYear"
              render={({field}) => (
                <FormItem className="flex-1">
                  <FormLabel className="sr-only">Year</FormLabel>
                  <Select
                    autoComplete="bday-year"
                    defaultValue={field.value}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Year" />
                      </SelectTrigger>
                    </FormControl>
                    <FormDescription className="sr-only">
                      Please select your birth year.
                    </FormDescription>
                    <SelectContent>
                      {YEARS.map((year) => (
                        <SelectItem key={year} value={String(year)}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>
          {form.formState.errors.birthYear && (
            <p className="text-destructive mt-0 text-[0.8rem] font-medium">
              {form.formState.errors.birthYear.message}
            </p>
          )}
        </div>
        <FormField
          control={form.control}
          name="password"
          render={({field}) => (
            <FormItem>
              <FormLabel>Create a password</FormLabel>
              <FormControl>
                <PasswordInput autoComplete="current-password" {...field} />
              </FormControl>
              <FormDescription className="sr-only">Please create a password.</FormDescription>
              <CreatePasswordRequirements isPasswordDirty={isPasswordDirty} password={password} />
            </FormItem>
          )}
        />
        <div className="my-6 flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="smsTransactional"
              render={({field}) => (
                <FormItem className="flex flex-row items-start gap-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        validateSmsPhoneRequirement();
                      }}
                      aria-label="Receive transactional SMS messages"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-medium">
                      Transactional text messages
                    </FormLabel>
                    <FormDescription className="text-muted-foreground text-sm">
                      I would like to receive personalized activity notifications regarding my
                      ClubOne Rewards account via text. Message and data rates may apply. (Please
                      make sure your mobile number is entered above.)
                    </FormDescription>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="smsPromotional"
              render={({field}) => (
                <FormItem className="flex flex-row items-start gap-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        validateSmsPhoneRequirement();
                      }}
                      aria-label="Receive promotional SMS messages"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-medium">Promotional text messages</FormLabel>
                    <FormDescription className="text-muted-foreground text-sm">
                      I would like to receive promotional notifications regarding my ClubOne Rewards
                      account via text. Message and data rates may apply. (Please make sure your
                      mobile number is entered above.)
                    </FormDescription>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-3 text-sm">
            <p>
              By joining ClubOne Rewards you provide consent to receive marketing and transactional
              emails from ClubOne Rewards. You also agree to the full{' '}
              <Link href="/terms-and-conditions" className="link" target="_blank">
                Terms &amp; Conditions
              </Link>{' '}
              and understand our{' '}
              <Link href="https://www.cochran.com/privacy-policy/" className="link" target="_blank">
                Privacy Policy
              </Link>
              .
            </p>
            <p>
              You can always update your email and text notification preferences in your profile
              settings.
            </p>
          </div>
        </div>
        <ErrorMessage errorMessage={errorMessage} />
        <LoadingButton className="mr-auto" loading={loading} type="submit">
          Create Account
        </LoadingButton>
        <div>
          <p>
            Already have an account?{' '}
            <Link href="/login" className="link">
              Sign In
            </Link>
          </p>
          <p className="text-sm">
            By signing up to create an account, I accept #1 Cochran ClubOne Rewards{' '}
            <Link href="/terms-and-conditions" className="link" target="_blank">
              Terms &amp; Conditions
            </Link>{' '}
            and{' '}
            <Link href="https://www.cochran.com/privacy-policy/" className="link" target="_blank">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </form>
    </Form>
  );
}
